import { Text } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import {
  useFlightPlanByCode,
  useFlightPlansManagementContext,
} from '@components/cockpit/panels/flightPlan/FlightPlansManagementContext';
import { Form } from '@components/ui/Form';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { useCreateFlightPlanMutation, useUpdateFlightPlanMutation } from '@services/utm/flightPlan.api';
import { areSelectedFieldsEqual, createErrorMessage } from '@utils/common.utils';
import { flightPlanSchemaValidation } from '@validations/utm.schema';
import { addDays, addMinutes } from 'date-fns';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { AbstractFlightPlan, ClassicFlightPlan, Provider } from '@/types/utm/flightPlan.types';

import FlightPlanFormMap from './FlightPlanFormMap';
import FlightPlanGeneralForm from './FlightPlanGeneralForm';

const emptyFlightPlan: Partial<ClassicFlightPlan> = {
  id: undefined,
  drones: [],
  minAltitudeFinite: false,
  maxAltitudeFinite: false,
  heightMax: null,
  heightMin: null,
  polygons: {
    type: 'MultiPolygon',
    coordinates: [],
  },
  name: '',
  startTime: addDays(new Date(), 1).toISOString(),
  endTime: addMinutes(addDays(new Date(), 1), 5).toISOString(),
  startPosition: null,
  provider: Provider.HOLOGARDE,
};

type FlightPlanDialogProps = {
  isNewFlightPlan: boolean;
  name: string | undefined;
} & Omit<CockpitDialogProps, 'children'>;

function FlightPlanDialog({
  isNewFlightPlan,
  name,
  open,
  isLoading,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<FlightPlanDialogProps>) {
  return (
    <CockpitDialog
      open={open}
      isLoading={isLoading}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id={`components.flightPlans.popup.${isNewFlightPlan ? 'descriptionSaveNew' : 'descriptionSave'}`}
          values={{ name }}
        />
      </Text>
    </CockpitDialog>
  );
}

export default function FlightPlanFormContainer() {
  const { formatMessage } = useIntl();
  const { editedFlightPlanCode, setEditedFlightPlanCode } = useFlightPlansManagementContext();
  const [open, setOpen] = useState(false);
  const flightPlan = useFlightPlanByCode(editedFlightPlanCode ?? '') ?? emptyFlightPlan;
  const activeSite = useAppSelector(selectActiveSite);
  const [createFlightPlan, { isLoading: isLoadingCreate }] = useCreateFlightPlanMutation();
  const [updateFlightPlan, { isLoading: isLoadingUpdate }] = useUpdateFlightPlanMutation();
  const isNewFlightPlan = editedFlightPlanCode === undefined;
  const validationSchema = useValidation(flightPlanSchemaValidation);
  const [showMapForm, setShowMapForm] = useState(false);

  const handleSubmit = (values: Partial<AbstractFlightPlan>) => {
    if (isNewFlightPlan) {
      createFlightPlan({ ...values, code: uuidv4(), site: activeSite?.code ?? 'local' } as AbstractFlightPlan)
        .unwrap()
        .then((createdFlightPlan) => {
          setOpen(false);
          createToast(
            formatMessage({ id: 'components.flightPlans.message.add' }, { name: createdFlightPlan.name }),
            ToastStatusEnum.SUCCESS,
          );
          setEditedFlightPlanCode(null);
        })
        .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
    } else {
      updateFlightPlan(values as AbstractFlightPlan)
        .unwrap()
        .then((updatedFlightPlan) => {
          setOpen(false);
          createToast(
            formatMessage({ id: 'components.flightPlans.message.update' }, { name: updatedFlightPlan.name }),
            ToastStatusEnum.SUCCESS,
          );
          setEditedFlightPlanCode(null);
        })
        .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
    }
  };

  const breadcrumbItems = useMemo(() => {
    return [
      {
        label: formatMessage({ id: 'components.flightPlans.title' }),
        action: () => setEditedFlightPlanCode(null),
      },
      {
        label: isNewFlightPlan ? formatMessage({ id: 'components.flightPlans.newFlightPlan' }) : flightPlan.name,
        action: showMapForm ? () => setShowMapForm(false) : undefined,
      },
      ...(showMapForm
        ? [
            {
              label: formatMessage({ id: 'components.flightPlans.volumeDefinition' }),
            },
          ]
        : []),
    ];
  }, [flightPlan.name, formatMessage, isNewFlightPlan, setEditedFlightPlanCode, showMapForm]);

  return (
    <Formik
      initialValues={flightPlan}
      validationSchema={validationSchema}
      validateOnBlur
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ initialValues, isValid, submitForm, values }) => {
        const hasGeneralTabChanged = !areSelectedFieldsEqual(values, initialValues, [
          'name',
          'startTime',
          'endTime',
          'organizations',
          'drones',
          'heightMin',
          'heightMax',
          'polygons',
        ]);

        const hasChanged = () => {
          return hasGeneralTabChanged;
        };

        const isLoading = isLoadingCreate || isLoadingUpdate;

        return (
          <Form style={{ width: '100%', height: '100%' }}>
            <CockpitEditorLayout
              dialog={
                <FlightPlanDialog
                  open={open}
                  isLoading={isLoading}
                  confirmLabel={formatMessage({ id: 'global.save' })}
                  cancelLabel={formatMessage({ id: 'global.cancel' })}
                  onConfirm={submitForm}
                  onCancel={() => setOpen(false)}
                  isNewFlightPlan={isNewFlightPlan}
                  name={values.name}
                />
              }
              breadcrumbs={<Breadcrumb items={breadcrumbItems} />}
              onBack={() => setEditedFlightPlanCode(null)}
              onSave={() => {
                if (!isValid || !hasChanged()) {
                  return;
                }
                if (flightPlan.id === undefined) {
                  void submitForm();
                } else {
                  setOpen(true);
                }
              }}
              isSaving={isLoading}
              isSaveDisabled={!isValid || !hasChanged()}
            >
              {showMapForm ? <FlightPlanFormMap /> : <FlightPlanGeneralForm setShowMapForm={setShowMapForm} />}
            </CockpitEditorLayout>
          </Form>
        );
      }}
    </Formik>
  );
}
